// import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useProjects } from '../../hooks/useProjects';
import { useNewProjectTour } from '../../hooks/useNewProjectTour';
import { NewProjectTour } from '../../components/onboarding/NewProjectTour';
import type { CreateProjectPayload } from '../../types/api.types';
import { ROUTES, SUPPORTED_LANGUAGES } from '../../config/constants';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export const ProjectCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('projects');
  const { createProject, isCreating } = useProjects();
  const { showTour, completeTour, skipTour } = useNewProjectTour();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateProjectPayload>({
    defaultValues: {
      projectName: '',
      projectLanguage: '',
      userLanguage: '',
    },
  });

  const onSubmit = async (data: CreateProjectPayload) => {
    try {
      await createProject(data);
      navigate(ROUTES.PROJECTS);
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(ROUTES.PROJECTS)}
        sx={{ mb: 3 }}
      >
        {t('backToProjects')}
      </Button>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('create.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {t('create.description')}
        </Typography>

        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
          <Controller
            name="projectName"
            control={control}
            rules={{ required: t('create.projectNameRequired') }}
            render={({ field }) => (
              <TextField
                {...field}
                data-tour="project-name"
                fullWidth
                label={t('projectName')}
                placeholder={t('create.projectNamePlaceholder')}
                error={!!errors.projectName}
                helperText={errors.projectName?.message}
                margin="normal"
                autoFocus
              />
            )}
          />

          <Controller
            name="projectLanguage"
            control={control}
            rules={{ required: t('create.projectLanguageRequired') }}
            render={({ field }) => (
              <FormControl
                data-tour="project-language"
                fullWidth
                margin="normal"
                error={!!errors.projectLanguage}
              >
                <InputLabel>{t('create.learningLanguage')}</InputLabel>
                <Select {...field} label={t('create.learningLanguage')}>
                  <MenuItem value="">
                    <em>{t('create.selectLanguage')}</em>
                  </MenuItem>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <MenuItem key={lang.code} value={lang.name}>
                      {lang.name} ({lang.nativeName})
                    </MenuItem>
                  ))}
                </Select>
                {errors.projectLanguage && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {errors.projectLanguage.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          <Controller
            name="userLanguage"
            control={control}
            rules={{ required: t('create.userLanguageRequired') }}
            render={({ field }) => (
              <FormControl
                data-tour="user-language"
                fullWidth
                margin="normal"
                error={!!errors.userLanguage}
              >
                <InputLabel>{t('create.nativeLanguage')}</InputLabel>
                <Select {...field} label={t('create.nativeLanguage')}>
                  <MenuItem value="">
                    <em>{t('create.selectLanguage')}</em>
                  </MenuItem>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <MenuItem key={lang.code} value={lang.name}>
                      {lang.name} ({lang.nativeName})
                    </MenuItem>
                  ))}
                </Select>
                {errors.userLanguage && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                    {errors.userLanguage.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isCreating}
              fullWidth
            >
              {isCreating ? t('create.creating') : t('create.submit')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate(ROUTES.PROJECTS)}
              disabled={isCreating}
            >
              {t('create.cancel')}
            </Button>
          </Box>
        </Box>
      </Paper>

      <NewProjectTour open={showTour} onComplete={completeTour} onSkip={skipTour} />
    </Container>
  );
};
